import React, { useState, useEffect } from 'react';
import AuthPage from './components/AuthPage';
import LayoutHeader from './components/LayoutHeader';
import LayoutSidebar from './components/LayoutSidebar';
import DashboardContent from './components/DashboardContent';
import DevicesContent from './components/DevicesContent';
import TutorialsContent from './components/TutorialsContent';
import UserProfile from './components/UserProfile';
import SubscriptionPage from './components/SubscriptionPage';
import PaymentPage from './components/PaymentPage';
import { log, error } from './utils/logger'; // Importar la nueva página de pago
import AssistantLauncher from './components/AssistantLauncher';
import AssistantPanel from './components/AssistantPanel';
import DashboardTour from './components/DashboardTour';
import ESP32Generator from './components/ESP32Generator';
import CustomDevicesContent from './components/CustomDevicesContent';
import DashboardPage from "./components/DashboardPage";


const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem('isLoggedIn') === 'true');
  const [userName, setUserName] = useState(localStorage.getItem('userName') || '');
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [userId, setUserId] = useState(localStorage.getItem('userId') ? parseInt(localStorage.getItem('userId')) : null);
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || 'client');
  const [userPlan, setUserPlan] = useState(localStorage.getItem('userPlan') || 'basic'); // Mantener 'basic' como default inicial
  const [currentPage, setCurrentPage] = useState(localStorage.getItem('currentPage') || 'dashboard');
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(localStorage.getItem('selectedDeviceId') ? parseInt(localStorage.getItem('selectedDeviceId')) : null);
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('isDarkMode') === 'true');
  const [currentPlanLimits, setCurrentPlanLimits] = useState({ devices: 0, remoteControl: false, dataHistory: 'none' });
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null); // Nuevo estado para el plan a pagar
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Nuevo estado para controlar la visibilidad del sidebar
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [showAssistantAlert, setShowAssistantAlert] = useState(true);



  const API_BASE_URL = 'https://holmio.net/app/api.php';
  const PAYPAL_API_URL = 'https://holmio.net/app/api_paypal.php'; // Tu API de PayPal

  useEffect(() => {
    localStorage.setItem('isLoggedIn', isLoggedIn);
    localStorage.setItem('userName', userName);
    localStorage.setItem('userEmail', userEmail);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userRole', userRole);
    localStorage.setItem('userPlan', userPlan);
    localStorage.setItem('currentPage', currentPage);
    localStorage.setItem('selectedDeviceId', selectedDeviceId);
    localStorage.setItem('isDarkMode', isDarkMode);
  }, [isLoggedIn, userName, userEmail, userId, userRole, userPlan, currentPage, selectedDeviceId, isDarkMode]);
  
  
  useEffect(() => {
    const fetchPlanLimits = async () => {
      if (!userPlan) return; // Asegurarse de que userPlan tenga un valor
      try {
        const response = await fetch(`${API_BASE_URL}/plans?id=${userPlan}`);
        const data = await response.json();
        if (data.status === 'success' && data.data.length > 0) {
          const plan = data.data[0];
          setCurrentPlanLimits({
            devices: plan.device_limit === -1 ? Infinity : plan.device_limit,
            remoteControl: plan.remote_control,
            dataHistory: plan.history_days === -1 ? 'unlimited' : `${plan.history_days}_days`,
          });
        } else {
          console.error('Error al cargar límites del plan:', data.message);
          // Establecer límites por defecto si hay un error o el plan no se encuentra
          setCurrentPlanLimits({ devices: 3, remoteControl: true, dataHistory: 'last' });
        }
      } catch (error) {
        console.error('Error de red o servidor al cargar límites del plan:', error);
        // Establecer límites por defecto en caso de error de red
        setCurrentPlanLimits({ devices: 3, remoteControl: true, dataHistory: 'last' });
      }
    };
    // Solo cargar los límites del plan si el usuario está logeado y el userId existe
    if (isLoggedIn && userId) {
      fetchPlanLimits();
    }
  }, [userPlan, isLoggedIn, userId]); // Añadir isLoggedIn y userId como dependencias

  useEffect(() => {
    const fetchDevices = async () => {
      if (!userId) return;

      try {
        const response = await fetch(`${API_BASE_URL}/devices?user_id=${userId}`);
        const data = await response.json();
        if (data.status === 'success') {
          const fetchedDevices = data.data.map(dev => ({
            ...dev,
            controls: typeof dev.controls === 'string' ? JSON.parse(dev.controls) : dev.controls
          }));
          const filteredDevices = fetchedDevices.filter(dev => dev.user_id == userId).slice(0, currentPlanLimits.devices);
          setDevices(filteredDevices);
          if (filteredDevices.length > 0 && !selectedDeviceId) {
            setSelectedDeviceId(filteredDevices[0].id);
          }
        } else {
          console.error(`Error al cargar dispositivos: ${data.message}`);
        }
      } catch (error) {
        console.error('Error de red o servidor al cargar dispositivos:', error);
      }
    };

    if (isLoggedIn && userId && currentPlanLimits.devices > 0) {
      fetchDevices();
    }
  }, [isLoggedIn, userId, setDevices, selectedDeviceId, currentPlanLimits.devices]);

  // Nuevo useEffect para cargar el email y el plan del usuario si ya está logeado
  useEffect(() => {
    const fetchUserData = async () => {
      if (isLoggedIn && userId) {
        try {
          const response = await fetch(`${API_BASE_URL}/users?id=${userId}`);
          const data = await response.json();
          if (data.status === 'success' && data.data) {
            setUserEmail(data.data.email);
            // Asegurarse de que el plan se cargue desde la DB y se actualice el estado
            const planFromDb = data.data.plan_id || 'basic';
            if (userPlan !== planFromDb) { // Solo actualizar si es diferente para evitar loops
              setUserPlan(planFromDb);
            }
            log("Plan del usuario cargado desde DB:", planFromDb); // Debugging
          } else {
            console.error('Error al cargar datos del usuario:', data.message);
          }
        } catch (error) {
          console.error('Error de red o servidor al cargar datos del usuario:', error);
        }
      }
    };
    fetchUserData();
  }, [isLoggedIn, userId, userPlan]); // Añadir userPlan como dependencia para re-ejecutar si cambia

  const handleLoginSuccess = (name, id, role = 'client', planId = 'basic', email) => {
    setUserName(name);
    setUserId(id);
    setUserRole(role);
    setUserPlan(planId);
    setUserEmail(email);
    setIsLoggedIn(true);
    setCurrentPage('dashboard');
  };

  const handleRegisterSuccess = () => {
    alert('¡Registro exitoso! Por favor, inicia sesión.'); // Mensaje de éxito
    // No redirigir automáticamente al dashboard aquí, ya que el usuario no ha iniciado sesión
    // La AuthPage se encargará de cambiar a la vista de login
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserName('');
    setUserEmail('');
    setUserId(null);
    setUserRole('client');
    setUserPlan('basic');
    setCurrentPage('dashboard');
    setDevices([]);
    setSelectedDeviceId(null);
    localStorage.clear();
  };

  const handleUpdateProfile = async (newName, newEmail, newPassword) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: userId, username: newName, email: newEmail, password: newPassword }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setUserName(newName);
        setUserEmail(newEmail);
        alert('Perfil actualizado con éxito.');
      } else {
        alert(`Error al actualizar perfil: ${data.message}`);
      }
    } catch (error) {
      console.error('Error de red o servidor al actualizar perfil:', error);
      alert('Error al conectar con el servidor para actualizar el perfil.');
    }
  };

  const handleDeviceSelect = (id) => {
    setSelectedDeviceId(id);
  };

  const getSelectedDevice = () => {
    return devices.find(dev => dev.id == selectedDeviceId);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(prevMode => !prevMode);
  };

  // Función para manejar la selección de un plan de pago
  const handleSelectPlanForPayment = (plan) => {
    setSelectedPlanForPayment(plan);
    setCurrentPage('payment'); // Cambiar a la página de pago
  };

  // Función para manejar el éxito del pago (desde PaymentPage)
  const handlePaymentSuccess = async (planId, paypalDetails) => {
    console.log("Enviando datos de pago a PAYPAL_API_URL:", PAYPAL_API_URL); // Debugging
    console.log("Datos a enviar:", {
      action: 'update_subscription',
      userId: userId,
      planId: planId,
      paypalOrderId: paypalDetails.id,
      status: paypalDetails.status,
      payerId: paypalDetails.payer.payer_id,
      payerEmail: paypalDetails.payer.email_address,
      amountPaid: paypalDetails.purchase_units[0].payments.captures[0].amount.value,
      currency: paypalDetails.purchase_units[0].payments.captures[0].amount.currency_code,
      rawResponse: paypalDetails,
    });

    try {
      const response = await fetch(PAYPAL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'update_subscription',
          userId: userId,
          planId: planId,
          paypalOrderId: paypalDetails.id,
          status: paypalDetails.status,
          payerId: paypalDetails.payer.payer_id, // Acceder directamente a payer_id
          payerEmail: paypalDetails.payer.email_address, // Acceder directamente a email_address
          amountPaid: paypalDetails.purchase_units[0].payments.captures[0].amount.value,
          currency: paypalDetails.purchase_units[0].payments.captures[0].amount.currency_code,
          rawResponse: paypalDetails,
        }),
      });

      // Leer la respuesta como texto primero para depuración
      const responseText = await response.text();
      console.log("Raw response from PAYPAL_API_URL:", responseText); // Debugging

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.error('Error parsing JSON response from PAYPAL_API_URL:', jsonError);
        alert('Error al procesar la respuesta del servidor de PayPal. Por favor, contacta a soporte.');
        return;
      }
      
      if (data.status === 'success') {
        alert('¡Suscripción actualizada con éxito!');
        setUserPlan(planId); // Actualizar el plan del usuario en App.js
        setCurrentPage('dashboard'); // Volver al dashboard
        setSelectedPlanForPayment(null); // Limpiar el plan seleccionado
      } else {
        alert(`Error al actualizar suscripción en el backend: ${data.message || 'Mensaje de error no disponible'}`);
      }
    } catch (error) {
      console.error('Error al comunicar el pago con el backend:', error);
      alert('Hubo un error al procesar tu suscripción. Por favor, contacta a soporte.');
    }
  };

  if (!isLoggedIn) {
    return <AuthPage onLoginSuccess={handleLoginSuccess} onRegisterSuccess={handleRegisterSuccess} setUserId={setUserId} />;
  }

  return (
    <div className={`flex flex-col min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <LayoutHeader
        userName={userName}
        onProfileClick={() => setCurrentPage('profile')}
        onLogout={handleLogout}
        devices={devices}
        selectedDevice={getSelectedDevice()}
        onDeviceSelect={handleDeviceSelect}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        userPlan={userPlan} // Asegurarse de pasar el userPlan actualizado
        onUpgradeClick={() => setCurrentPage('subscription')}
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} // Pasar la función para alternar el sidebar
      />
      <div className="flex flex-1">
        <LayoutSidebar 
          currentPage={currentPage} 
          onNavigate={(page) => {
            setCurrentPage(page);
            setIsSidebarOpen(false); // Cerrar el sidebar al navegar
          }} 
          isDarkMode={isDarkMode} 
          userRole={userRole} 
          isOpen={isSidebarOpen} // Pasar el estado de apertura
          onClose={() => setIsSidebarOpen(false)} // Pasar la función para cerrar
        />
        {currentPage === 'dashboard' && (
          <DashboardPage isDarkMode={isDarkMode} />
        )}

        {currentPage === 'devices' && (
          <DevicesContent
            devices={devices}
            setDevices={setDevices}
            userId={userId}
            isDarkMode={isDarkMode}
            currentPlanLimits={currentPlanLimits}
          />
        )}
          {currentPage === 'customDevices' && (
          <CustomDevicesContent
            devices={devices}
            setDevices={setDevices}
            userId={userId}
            isDarkMode={isDarkMode}
            currentPlanLimits={currentPlanLimits}
          />
        )}


        {currentPage === 'tutorials' && <TutorialsContent isDarkMode={isDarkMode} />}
        {currentPage === 'profile' && (
          <UserProfile
            userName={userName}
            userEmail={userEmail}
            onUpdateProfile={handleUpdateProfile}
            onBack={() => setCurrentPage('dashboard')}
            isDarkMode={isDarkMode}
            userRole={userRole}
            userPlan={userPlan}
          />
        )}
        {currentPage === 'subscription' && (
          <SubscriptionPage
            currentPlan={userPlan}
            onUpgradePlan={(planId) => {
              setUserPlan(planId); // Actualizar el plan directamente si es gratis
              setCurrentPage('dashboard');
            }}
            isDarkMode={isDarkMode}
            userId={userId}
            onSelectPlanForPayment={handleSelectPlanForPayment} // Pasar la nueva función
          />
        )}
        {currentPage === 'payment' && selectedPlanForPayment && (
          <PaymentPage
            isDarkMode={isDarkMode}
            onBack={() => setCurrentPage('subscription')}
            planDetails={selectedPlanForPayment}
            userId={userId}
            onPaymentSuccess={handlePaymentSuccess} // Pasar la función de éxito de pago
          />
        )}
         {currentPage === 'esp32' && 
         <ESP32Generator 
         isDarkMode={isDarkMode} 
         userId={userId}
         />
         } 
        <AssistantLauncher
          onClick={() => {
            setIsAssistantOpen(true);
            setShowAssistantAlert(false);
          }}
          showAlert={showAssistantAlert}
          userPlan={userPlan}
        />

          {isAssistantOpen && (
            <AssistantPanel
            onClose={() => setIsAssistantOpen(false)}
            userPlan={userPlan}
            onAction={(command) => {
              if (command === 'goto_dashboard') setCurrentPage('dashboard');
              if (command === 'goto_devices') setCurrentPage('devices');
              if (command === 'goto_profile') setCurrentPage('profile');
              if (command === 'goto_tutorials') setCurrentPage('tutorials');
              if (command === 'goto_subscription') setCurrentPage('subscription');
            }}
          />
          )}
          <DashboardTour run={isTourRunning} />

      </div>
    </div>
  );
};

export default App;

// DONEs